/**
 * A small, pure Markdown parser for wiki pages and assistant answers. It never produces HTML:
 * the renderer builds React elements from these nodes, so raw HTML stays text. Every inline
 * node keeps its `[start, end)` offsets (UTF-16 code units) in the raw string, so marks computed
 * on the raw text (citation ranges, unverified statements) land on the right words whatever the
 * formatting. The raw string is never rewritten: CRLF line ends are skipped, not replaced.
 *
 * Supported: ATX headings, paragraphs with line breaks, ordered and unordered lists with one
 * nested level, block quotes, fenced code (an unclosed fence runs to the end), GFM pipe tables
 * (a header and a delimiter row, else the lines stay a paragraph), thematic breaks, and inline
 * code, links, `[[wiki links]]`, bold and italic. Images become their alt text.
 */

export type Inline =
  | { type: "text"; text: string; start: number; end: number }
  | { type: "break"; start: number; end: number }
  | { type: "strong" | "em"; children: Inline[]; start: number; end: number }
  | { type: "code"; text: string; start: number; end: number }
  /** `href` is null for a URL that is not `http(s)` or `mailto`: the label shows as text. */
  | { type: "link"; href: string | null; children: Inline[]; start: number; end: number }
  | { type: "wikilink"; title: string; label: string; start: number; end: number }
  /** A span the caller recognised (citation handles): rendered by the caller. */
  | { type: "atom"; raw: string; start: number; end: number };

export interface ListItem {
  content: Inline[];
  /** The nested lists under this item (one level), in order: a type change starts another. */
  children: Extract<Block, { type: "list" }>[];
}

export type Align = "left" | "center" | "right" | null;

export type Block =
  | { type: "heading"; depth: number; content: Inline[] }
  | { type: "paragraph"; content: Inline[] }
  | { type: "list"; ordered: boolean; start: number; items: ListItem[] }
  | { type: "quote"; content: Inline[] }
  /**
   * A fenced (or unclosed, while streaming) code block. `lang` is the info string's first word,
   * lower case, when it is a plain name (`mermaid`, `ts`); `closed` once its closing fence came.
   */
  | { type: "code"; text: string; lang?: string; closed?: true }
  | { type: "table"; align: Align[]; header: Inline[][]; rows: Inline[][][] }
  | { type: "rule" };

export interface ParseOptions {
  /**
   * Inline atoms by raw start offset → raw end offset (e.g. citation handles found on the raw
   * text). Matched before any other inline syntax, never inside code.
   */
  atoms?: ReadonlyMap<number, number>;
}

export const SAFE_URL = /^(https?:\/\/|mailto:)/i;

interface Line {
  start: number;
  /** End of the line's content: before `\n` and any `\r`. */
  end: number;
  text: string;
}

interface Segment {
  start: number;
  end: number;
}

function splitLines(raw: string): Line[] {
  const lines: Line[] = [];
  let start = 0;
  while (start <= raw.length) {
    const nl = raw.indexOf("\n", start);
    const stop = nl === -1 ? raw.length : nl;
    const end = stop > start && raw[stop - 1] === "\r" ? stop - 1 : stop;
    lines.push({ start, end, text: raw.slice(start, end) });
    if (nl === -1) {
      break;
    }
    start = nl + 1;
  }
  return lines;
}

const FENCE = /^ {0,3}(`{3,}|~{3,})/;
/** A fence language worth keeping: a plain name, not an arbitrary info string. */
const FENCE_LANGUAGE = /^[a-z0-9][\w+.#-]{0,31}$/;

/** The info string's first word, lower case, when it is a plain language name. */
function fenceLanguage(info: string): string | undefined {
  const word = info.trim().split(/\s+/, 1)[0]?.toLowerCase() ?? "";
  return FENCE_LANGUAGE.test(word) ? word : undefined;
}
const HEADING = /^( {0,3}#{1,6})(?:[ \t]+(.*))?$/;
const RULE = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const ITEM = /^( *)([-*+]|\d{1,9}[.)])(?:[ \t]+|$)/;
const QUOTE = /^ {0,3}> ?/;
const DELIMITER = /^ {0,3}\|?[ \t]*:?-+:?[ \t]*(?:\|[ \t]*:?-+:?[ \t]*)*\|?[ \t]*$/;

/** A heading's text without its optional closing `#`s and trailing spaces (no backtracking). */
function headingContent(body: string): string {
  let end = body.length;
  while (end > 0 && /[ \t]/.test(body[end - 1] ?? "")) {
    end--;
  }
  let hashes = end;
  while (hashes > 0 && body[hashes - 1] === "#") {
    hashes--;
  }
  if (hashes < end && (hashes === 0 || /[ \t]/.test(body[hashes - 1] ?? ""))) {
    end = hashes;
    while (end > 0 && /[ \t]/.test(body[end - 1] ?? "")) {
      end--;
    }
  }
  return body.slice(0, end);
}

const blank = (line: Line | undefined) => line === undefined || line.text.trim() === "";

/** Segments of a table row's cells, outer pipes dropped; pipes in code or escaped do not split. */
function cells(line: Line): Segment[] {
  let from = 0;
  let to = line.text.length;
  while (from < to && /\s/.test(line.text[from] ?? "")) {
    from++;
  }
  while (to > from && /\s/.test(line.text[to - 1] ?? "")) {
    to--;
  }
  if (line.text[from] === "|") {
    from++;
  }
  if (to > from && line.text[to - 1] === "|" && line.text[to - 2] !== "\\") {
    to--;
  }
  const out: Segment[] = [];
  let cell = from;
  let inCode = false;
  for (let i = from; i <= to; i++) {
    const ch = line.text[i];
    if (ch === "`") {
      inCode = !inCode;
    }
    if (i === to || (ch === "|" && !inCode && line.text[i - 1] !== "\\")) {
      let s = cell;
      let e = i;
      while (s < e && /\s/.test(line.text[s] ?? "")) {
        s++;
      }
      while (e > s && /\s/.test(line.text[e - 1] ?? "")) {
        e--;
      }
      out.push({ start: line.start + s, end: line.start + e });
      cell = i + 1;
    }
  }
  return out;
}

function isTableStart(lines: Line[], i: number): boolean {
  const head = lines[i];
  const delim = lines[i + 1];
  // Bounded and trimmed first: the delimiter pattern must not backtrack over long lines.
  if (
    !head ||
    !delim ||
    !head.text.includes("|") ||
    delim.text.length > 2000 ||
    !DELIMITER.test(delim.text.trimEnd())
  ) {
    return false;
  }
  return cells(head).length === cells(delim).length;
}

/** Whether a line starts a block other than a paragraph (so it ends a paragraph). */
function startsBlock(lines: Line[], i: number): boolean {
  const text = lines[i]?.text ?? "";
  return (
    FENCE.test(text) ||
    HEADING.test(text) ||
    RULE.test(text) ||
    QUOTE.test(text) ||
    ITEM.test(text) ||
    isTableStart(lines, i)
  );
}

export function parseMarkdown(raw: string, options: ParseOptions = {}): Block[] {
  const lines = splitLines(raw);
  const atoms = options.atoms ?? new Map<number, number>();
  const inline = (segments: Segment[]) => parseInline(raw, segments, atoms);
  const blocks: Block[] = [];
  let i = 0;

  const parseList = (
    from: number,
    depth: number,
  ): { block: Block & { type: "list" }; next: number } => {
    const first = ITEM.exec(lines[from]?.text ?? "") as RegExpExecArray;
    const indent = first[1]?.length ?? 0;
    const ordered = /\d/.test(first[2] ?? "");
    const block: Block & { type: "list" } = {
      type: "list",
      ordered,
      start: ordered ? Number.parseInt(first[2] ?? "1", 10) : 1,
      items: [],
    };
    const segments: Segment[][] = [];
    let at = from;
    while (at < lines.length) {
      const line = lines[at] as Line;
      if (blank(line)) {
        // A blank line between two items of this list keeps the list going.
        let next = at + 1;
        while (next < lines.length && blank(lines[next])) {
          next++;
        }
        const m = ITEM.exec(lines[next]?.text ?? "");
        if (m && (m[1]?.length ?? 0) <= indent + 1 && /\d/.test(m[2] ?? "") === ordered) {
          at = next;
          continue;
        }
        break;
      }
      const m = ITEM.exec(line.text);
      const level = m ? (m[1]?.length ?? 0) : -1;
      if (m && depth > 0 && level < indent) {
        // Back to the parent list's level: the nested list ends.
        break;
      }
      if (m && (level <= indent + 1 || depth > 0)) {
        if (/\d/.test(m[2] ?? "") !== ordered) {
          break;
        }
        block.items.push({ content: [], children: [] });
        segments.push([{ start: line.start + m[0].length, end: line.end }]);
        at++;
        continue;
      }
      const last = block.items.at(-1);
      if (m && last && depth === 0) {
        const nested = parseList(at, depth + 1);
        const previous = last.children.at(-1);
        if (previous && previous.ordered === nested.block.ordered) {
          previous.items.push(...nested.block.items);
        } else {
          last.children.push(nested.block);
        }
        at = nested.next;
        continue;
      }
      // An indented line continues the last item's text.
      const lead = /^[ \t]*/.exec(line.text)?.[0].length ?? 0;
      if (last && lead >= 2 && !startsBlock(lines, at)) {
        segments.at(-1)?.push({ start: line.start + lead, end: line.end });
        at++;
        continue;
      }
      break;
    }
    block.items.forEach((item, n) => {
      item.content = inline(segments[n] ?? []);
    });
    return { block, next: at };
  };

  while (i < lines.length) {
    const line = lines[i] as Line;
    if (blank(line)) {
      i++;
      continue;
    }
    const fence = FENCE.exec(line.text);
    if (fence) {
      const marker = fence[1] as string;
      const close = new RegExp(
        `^ {0,3}${marker[0] === "`" ? "`" : "~"}{${marker.length},}[ \\t]*$`,
      );
      const lang = fenceLanguage(line.text.slice(fence[0].length));
      const code: string[] = [];
      i++;
      while (i < lines.length && !close.test(lines[i]?.text ?? "")) {
        code.push(lines[i]?.text ?? "");
        i++;
      }
      // Tolerant: an unclosed fence runs to the end of the text (a streaming answer).
      const closed = i < lines.length;
      i++;
      blocks.push({
        type: "code",
        text: code.join("\n"),
        ...(lang ? { lang } : {}),
        ...(closed ? { closed: true as const } : {}),
      });
      continue;
    }
    const heading = HEADING.exec(line.text);
    if (heading) {
      const prefix = (heading[1] as string).length;
      const body = heading[2] ?? "";
      const lead = line.text.length - prefix - body.length;
      // Optional closing `#`s and trailing spaces are not content.
      const content = headingContent(body);
      const start = line.start + prefix + lead;
      blocks.push({
        type: "heading",
        depth: prefix - (/^ */.exec(line.text)?.[0].length ?? 0),
        content: inline([{ start, end: start + content.length }]),
      });
      i++;
      continue;
    }
    if (RULE.test(line.text)) {
      blocks.push({ type: "rule" });
      i++;
      continue;
    }
    if (isTableStart(lines, i)) {
      const align = cells(lines[i + 1] as Line).map((s): Align => {
        const spec = raw.slice(s.start, s.end);
        const left = spec.startsWith(":");
        const right = spec.endsWith(":");
        return left && right ? "center" : right ? "right" : left ? "left" : null;
      });
      const width = align.length;
      const row = (l: Line) => {
        const found = cells(l).slice(0, width);
        while (found.length < width) {
          found.push({ start: l.end, end: l.end });
        }
        return found.map((s) => inline([s]));
      };
      const header = row(line);
      const rows: Inline[][][] = [];
      i += 2;
      while (i < lines.length && !blank(lines[i]) && lines[i]?.text.includes("|")) {
        if (FENCE.test(lines[i]?.text ?? "") || HEADING.test(lines[i]?.text ?? "")) {
          break;
        }
        rows.push(row(lines[i] as Line));
        i++;
      }
      blocks.push({ type: "table", align, header, rows });
      continue;
    }
    if (ITEM.test(line.text)) {
      const { block, next } = parseList(i, 0);
      blocks.push(block);
      i = next;
      continue;
    }
    if (QUOTE.test(line.text)) {
      const segments: Segment[] = [];
      while (i < lines.length && QUOTE.test(lines[i]?.text ?? "")) {
        const l = lines[i] as Line;
        const prefix = (QUOTE.exec(l.text) as RegExpExecArray)[0].length;
        segments.push({ start: l.start + prefix, end: l.end });
        i++;
      }
      blocks.push({ type: "quote", content: inline(segments) });
      continue;
    }
    const segments: Segment[] = [];
    while (
      i < lines.length &&
      !blank(lines[i]) &&
      (segments.length === 0 || !startsBlock(lines, i))
    ) {
      const l = lines[i] as Line;
      const lead = /^[ \t]*/.exec(l.text)?.[0].length ?? 0;
      segments.push({ start: l.start + lead, end: l.end });
      i++;
    }
    blocks.push({ type: "paragraph", content: inline(segments) });
  }
  return blocks;
}

// ── Inline ───────────────────────────────────────────────────────────────────────────────

const CODE = /`([^`\n]+)`/y;
// URLs may hold one level of balanced parentheses (`javascript:alert(1)` stays one URL).
const URL_PART = String.raw`((?:[^()\s]|\([^()\s]{0,200}\)){0,2000})`;
const IMAGE = new RegExp(String.raw`!\[([^\]\n]*)\]\(` + URL_PART + String.raw`\)`, "y");
const WIKILINK = /\[\[([^[\]|\n]{1,200})(?:\|([^[\]\n]{0,200}))?\]\]/y;
const LINK = new RegExp(String.raw`\[([^\]\n]{1,1000})\]\(` + URL_PART + String.raw`\)`, "y");
const STRONG = /\*\*(?!\s)([^\n]{1,1000}?)(?<!\s)\*\*/y;
const EM = /\*(?!\s|\*)([^*\n]{1,1000}?)(?<!\s)\*/y;

/**
 * Inline content of a block made of `segments` (raw ranges, one per source line). Lines are
 * joined by a break node; every text node lies inside one segment, so its raw range is exact.
 */
function parseInline(
  raw: string,
  segments: readonly Segment[],
  atoms: ReadonlyMap<number, number>,
): Inline[] {
  const virtual = segments.map((s) => raw.slice(s.start, s.end)).join("\n");
  const starts: number[] = [];
  let at = 0;
  for (const s of segments) {
    starts.push(at);
    at += s.end - s.start + 1;
  }
  /** Raw offset of virtual index `v` (a joining `\n` maps to the end of its line). */
  /** Raw offset of virtual index `v`; a binary search keeps long multi-line blocks linear. */
  const toRaw = (v: number) => {
    let lo = 0;
    let hi = starts.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if ((starts[mid] as number) <= v) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }
    const k = lo;
    const seg = segments[k] as Segment;
    return Math.min(seg.start + (v - (starts[k] as number)), seg.end);
  };

  // The next occurrence of each closing marker, cached: an opener with no closer later on is
  // skipped without running its pattern, which keeps unclosed `[` or `**` runs linear.
  const nextAt = new Map<string, number>();
  const closes = (marker: string, from: number) => {
    const known = nextAt.get(marker);
    if (known !== undefined && (known === -1 || known >= from)) {
      return known !== -1;
    }
    const found = virtual.indexOf(marker, from);
    nextAt.set(marker, found);
    return found !== -1;
  };

  /** The inline syntax starting at virtual index `i` and ending by `to`, if any. */
  const matchAt = (i: number, to: number): { length: number; node: Inline | null } | null => {
    const sticky = (re: RegExp) => {
      re.lastIndex = i;
      const m = re.exec(virtual);
      return m && i + m[0].length <= to ? m : null;
    };
    const span = (m: RegExpExecArray) => ({ start: toRaw(i), end: toRaw(i + m[0].length) });
    const ch = virtual[i];
    if (
      (ch === "`" && !closes("`", i + 1)) ||
      ((ch === "[" || ch === "!") && !closes("]", i + 1)) ||
      (ch === "*" && !closes("*", i + 1))
    ) {
      return null;
    }
    if (ch === "`") {
      const m = sticky(CODE);
      return m && { length: m[0].length, node: { type: "code", text: m[1] as string, ...span(m) } };
    }
    if (ch === "!") {
      const m = sticky(IMAGE);
      // Images are not loaded: the alt text stands in.
      const alt = m?.[1] ?? "";
      return (
        m && {
          length: m[0].length,
          node: alt
            ? { type: "text", text: alt, start: toRaw(i + 2), end: toRaw(i + 2 + alt.length) }
            : null,
        }
      );
    }
    if (ch === "[") {
      const wiki = virtual[i + 1] === "[" ? sticky(WIKILINK) : null;
      if (wiki) {
        const title = (wiki[1] as string).trim();
        return {
          length: wiki[0].length,
          node: { type: "wikilink", title, label: wiki[2]?.trim() || title, ...span(wiki) },
        };
      }
      const m = sticky(LINK);
      if (!m) {
        return null;
      }
      const label = m[1] as string;
      const url = m[2] as string;
      return {
        length: m[0].length,
        node: {
          type: "link",
          href: SAFE_URL.test(url) ? url : null,
          children: scan(i + 1, i + 1 + label.length),
          ...span(m),
        },
      };
    }
    if (ch === "*") {
      const strong = virtual[i + 1] === "*" ? sticky(STRONG) : null;
      if (strong) {
        const children = scan(i + 2, i + strong[0].length - 2);
        return { length: strong[0].length, node: { type: "strong", children, ...span(strong) } };
      }
      const m = sticky(EM);
      return (
        m && {
          length: m[0].length,
          node: { type: "em", children: scan(i + 1, i + m[0].length - 1), ...span(m) },
        }
      );
    }
    return null;
  };

  function scan(from: number, to: number): Inline[] {
    const out: Inline[] = [];
    let text = from;
    let i = from;
    const flush = (upTo: number) => {
      if (upTo > text) {
        out.push({
          type: "text",
          text: virtual.slice(text, upTo),
          start: toRaw(text),
          end: toRaw(upTo),
        });
      }
    };
    while (i < to) {
      const ch = virtual[i];
      if (ch === "\n") {
        flush(i);
        out.push({ type: "break", start: toRaw(i), end: toRaw(i + 1) });
        i++;
        text = i;
        continue;
      }
      const atomEnd = atoms.get(toRaw(i));
      if (atomEnd !== undefined) {
        const length = atomEnd - toRaw(i);
        const end = i + length;
        if (end <= to && !virtual.slice(i, end).includes("\n")) {
          flush(i);
          out.push({ type: "atom", raw: virtual.slice(i, end), start: toRaw(i), end: toRaw(end) });
          i = end;
          text = i;
          continue;
        }
      }
      const found = matchAt(i, to);
      if (found) {
        flush(i);
        if (found.node) {
          out.push(found.node);
        }
        i += found.length;
        text = i;
        continue;
      }
      i++;
    }
    flush(to);
    return out;
  }
  return scan(0, virtual.length);
}

/** The text of inline nodes, markup dropped (atoms by their raw text, breaks as nothing). */
export function plainText(nodes: readonly Inline[]): string {
  return nodes
    .map((n) =>
      n.type === "text" || n.type === "code"
        ? n.text
        : n.type === "atom"
          ? n.raw
          : n.type === "wikilink"
            ? n.label
            : "children" in n
              ? plainText(n.children)
              : "",
    )
    .join("");
}
